import {gql} from '@apollo/client'
import {ChildcareMethod} from '../ChildcareMethodSelect/query'

export const APPLICATION_TMP_DATA_NAME = 'childcare_info'

// release 前に作成された在途手続き（procedure_childcare_datum 未作成）の共通項目を
// 復元するための旧 application tmp
// - procedure_childcare（R507/R508 社保）: 子情報・開始日・終了予定日
// - r509/r512_param_set（雇保）: 雇保のみ手続きの開始日・出産年月日、および出産予定日
const LEGACY_APPLICATION_TMP_FRAGMENT = gql`
  fragment LegacyApplicationTmpFragment on ProcedureStatus {
    applicationChildcare: procedureTmpDatum(type: application, name: "procedure_childcare") {
      id
      data
    }
    applicationR509: procedureTmpDatum(type: application, name: "r509_param_set") {
      id
      data
    }
    applicationR512: procedureTmpDatum(type: application, name: "r512_param_set") {
      id
      data
    }
  }
`

export const PROCEDURE_CHILDCARE_INFO_FORM = gql`
  query procedureChildcareInfoForm($procedureStatusId: ID!, $procedureTmpType: ProcedureTmpType!) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        procedureType
        childcareExpectedBirthOn
        employee {
          id
          staffCode
          displayFullName
          employeeDependents {
            id
            firstName
            lastName
            firstNameKana
            lastNameKana
            birthday
            relationName
            relationReportType
          }
        }
        childcareMethodSelect: procedureTmpDatum(type: $procedureTmpType, name: "childcare_method_select") {
          id
          data
        }
        applicationChildcareInfo: procedureTmpDatum(type: application, name: "${APPLICATION_TMP_DATA_NAME}") {
          id
          type
          name
          data
        }
        ...LegacyApplicationTmpFragment
        procedureChildcareDatum {
          id
          employeeDependent {
            id
          }
          startedOn
          expectedFinishedOn
          addToLeaveOfAbsence
          leaveOfAbsenceReflected
          procedureChildcareChildDatum {
            id
            firstName
            lastName
            firstNameKana
            lastNameKana
            birthDate
            relation
            parentingStartedDate
            addSupport
          }
        }
      }
    }
  }
  ${LEGACY_APPLICATION_TMP_FRAGMENT}
`

export interface ProcedureChildcareInfoFormQueryVariables {
  procedureStatusId: string
  procedureTmpType: 'draft' | 'employee_draft'
}

export interface ProcedureChildcareInfoFormQueryResult {
  client: {
    id: string
    procedureStatus: {
      id: string
      procedureType: string
      childcareExpectedBirthOn?: string
      employee: {
        id: string
        staffCode: string
        displayFullName: string
        employeeDependents: {
          id: string
          firstName: string
          lastName: string
          firstNameKana?: string
          lastNameKana?: string
          birthday?: string
          relationName?: string
          relationReportType?: string
        }[]
      }
      childcareMethodSelect?: {
        id: string
        data: {method?: ChildcareMethod}
      }
      applicationChildcareInfo?: {
        id: string
        type: string
        name: string
        data: unknown
      }
      applicationChildcare?: {
        id: string
        data: unknown
      }
      applicationR509?: {
        id: string
        data: unknown
      }
      applicationR512?: {
        id: string
        data: unknown
      }
      procedureChildcareDatum?: {
        id: string
        employeeDependent?: {
          id: string
        }
        startedOn: string
        expectedFinishedOn?: string
        addToLeaveOfAbsence: boolean
        leaveOfAbsenceReflected: boolean
        procedureChildcareChildDatum?: {
          id: string
          firstName?: string
          lastName?: string
          firstNameKana?: string
          lastNameKana?: string
          birthDate?: string
          relation: 'real' | 'other'
          parentingStartedDate?: string
          addSupport: boolean
        }
      }
    }
  }
}

export const UPDATE_CHILDCARE_INFO_FORM_DATUM = gql`
  mutation updateChildcareInfoFormDatum($input: UpdateProcedureChildcareDatumInput!) {
    updateProcedureChildcareDatum(input: $input) {
      procedureChildcareDatum {
        id
      }
    }
  }
`

// ChildcareInfoForm が管理する共通項目のみを送る（leaveData / 帳票項目は送らず、
// バックエンドの assign_attributes / blank? skip により R5xx 側の値を温存する）。
export interface UpdateChildcareInfoFormDatumQueryVariables {
  input: {
    procedureStatusId: string
    attributes: {
      employeeDependentId?: string | null
      startedOn: string
      expectedFinishedOn?: string | null
      expectedBirthOn?: string | null
      // 管理者のみ送信（member が送ると FORBIDDEN）
      addToLeaveOfAbsence?: boolean
      procedureChildcareChildDatum: {
        firstName?: string | null
        lastName?: string | null
        firstNameKana?: string | null
        lastNameKana?: string | null
        birthDate: string
        relation?: 'real' | 'other' | null
        parentingStartedDate?: string | null
        addSupport?: boolean
      }
    }
  }
}

export const UPDATE_CHILDCARE_INFO_FORM_TMP_DATA = gql`
  mutation updateChildcareInfoFormTmpData($input: UpdateProcedureTmpDatumInput!) {
    updateProcedureTmpDatum(input: $input) {
      procedureStatus {
        id
      }
    }
  }
`

export interface UpdateChildcareInfoFormTmpDataQueryVariables {
  input: {
    procedureStatusId: string
    type: 'application'
    name: string
    data: unknown
  }
}

// mode='from_past' 専用: 過去手続きドロップダウン + 反映元データ。
// forProcedureType は当該手続きの procedureType（呼び出し側が procedureStatus.procedureType を渡す）。
export const PAST_CHILDCARE_PROCEDURES_FOR_REFLECT = gql`
  query pastChildcareProceduresForReflect($procedureStatusId: ID!, $forProcedureType: ProcedureType!) {
    client {
      id
      procedureStatus(procedureStatusId: $procedureStatusId) {
        id
        employee {
          id
          pastChildcareProcedureStatuses(forProcedureType: $forProcedureType) {
            id
            procedureType
            procedureChildcareDatum {
              id
              startedOn
              expectedFinishedOn
              expectedBirthOn
              procedureChildcareChildDatum {
                id
                firstName
                lastName
                firstNameKana
                lastNameKana
                birthDate
                relation
                parentingStartedDate
              }
            }
          }
        }
      }
    }
  }
`

export interface PastChildcareProceduresForReflectQueryVariables {
  procedureStatusId: string
  forProcedureType: string
}

export interface PastChildcareProcedureForReflect {
  id: string
  procedureType: string
  procedureChildcareDatum?: {
    id: string
    startedOn: string
    expectedFinishedOn?: string
    expectedBirthOn?: string
    procedureChildcareChildDatum?: {
      id: string
      firstName?: string
      lastName?: string
      firstNameKana?: string
      lastNameKana?: string
      birthDate?: string
      relation: 'real' | 'other'
      parentingStartedDate?: string
    }
  }
}

export interface PastChildcareProceduresForReflectQueryResult {
  client: {
    id: string
    procedureStatus?: {
      id: string
      employee: {
        id: string
        pastChildcareProcedureStatuses: PastChildcareProcedureForReflect[]
      }
    }
  }
}
